// @flow strict
import { type Feature } from './PlusBundles'
import { type PaidFeaturesComparisonTableRow } from './ComparisonTable'
import Placeholder from '../../../../static/images/icons/placeholder.png'

export const featuresToObject = (
  features: Feature[],
  icons?: { [k: string]: string },
): { [k: string]: Feature } => {
  return features.reduce((acc, { value, ...feature }: Feature) => {
    if (feature.id != null && feature.title != null) {
      acc[feature.id] = {
        title: feature.title || '',
        description: feature.description || '',
        icon:
          (icons && feature.icon && icons[feature.icon]) ||
          (icons && icons.check) ||
          Placeholder,
        value,
      }
    }
    return acc
  }, {})
}

// This function basically does all the heavy lifting to turn
// some contentful data into easily table-ified shapes so we don't
// have to duplicate the data at the source
export const featuresToTable = (
  featuresMap: { [k: string]: Feature },
  table: { core: string[], holo: string[], metal?: string[] },
): PaidFeaturesComparisonTableRow[] | null => {
  let tableShapedFeatureData = []

  // this helps keep track of features that exist across the board but with values which differ across bundles
  let alreadySeenFeatures = []

  // It iterates through each of the bundles and adds those features to the list of table rows
  if (table.core) {
    table.core.forEach(featureId => {
      const feature = featuresMap[featureId]
      if (feature == null) return

      // This bit identifies those features that aren't just a boolean existence
      // (ie have a price/limit) which is indicated by a `:` in the id
      if (featureId.indexOf(':') > -1) {
        // Get thee "base" id
        const parentFeatureId = featureId.split(':')[0]

        // Look for a matching id in the Monzo Plus column
        const holoFeatureId = table.holo.find(
          fId => fId.split(':')[0] === parentFeatureId,
        )

        const metalFeatureId =
          table.metal &&
          table.metal.find(fId => fId.split(':')[0] === parentFeatureId)

        const parentFeatureName =
          parentFeatureId &&
          featuresMap[parentFeatureId] &&
          featuresMap[parentFeatureId].title
        const holoValue =
          holoFeatureId &&
          featuresMap[holoFeatureId] &&
          featuresMap[holoFeatureId].value
        const metalValue =
          metalFeatureId &&
          featuresMap[metalFeatureId] &&
          featuresMap[metalFeatureId].value

        alreadySeenFeatures.push(parentFeatureId)

        tableShapedFeatureData.push({
          name: parentFeatureName || '',
          core: feature.value || '',
          holo: holoValue || '',
          metal: metalValue || '',
        })

        return
      }

      // This is the default return which just marks a feature as included or not
      tableShapedFeatureData.push({
        name: feature.title || '',
        core: true,
        holo: true,
        metal: true,
      })
    })
  }

  // The second bundle repeats some bits since some features only have values in the later bundles
  if (table.holo) {
    table.holo.forEach(featureId => {
      const feature = featuresMap[featureId]
      if (feature == null) return null

      const parentFeatureId = featureId.split(':')[0]

      // If it's a value feature (and not a simple yes/no)
      if (featureId.indexOf(':') > -1) {
        // and if we haven't yet included it
        if (
          alreadySeenFeatures.find(
            seenFeatureId => seenFeatureId === parentFeatureId,
          ) == null
        ) {
          const metalFeatureId =
            table.metal &&
            table.metal.find(fId => fId.split(':')[0] === parentFeatureId)
          const parentFeatureName =
            parentFeatureId &&
            featuresMap[parentFeatureId] &&
            featuresMap[parentFeatureId].title
          const metalValue =
            metalFeatureId &&
            featuresMap[metalFeatureId] &&
            featuresMap[metalFeatureId].value

          tableShapedFeatureData.push({
            name: parentFeatureName || '',
            core: false,
            holo: feature.value || '',
            metal: metalValue || '',
          })
          return
        }
      } else {
        // It's just a yes/no feature
        tableShapedFeatureData.push({
          name: feature.title || '',
          core: false,
          holo: true,
          metal: true,
        })
      }
    })
  }

  // By the time we get here there's not much more left to do

  if (table.metal != null && table.metal.length > 0) {
    table.metal.forEach(featureId => {
      const feature = featuresMap[featureId]
      if (feature == null) return
      const parentFeatureId = featureId.split(':')[0]
      if (
        featureId.indexOf(':') > -1 ||
        alreadySeenFeatures.includes(parentFeatureId)
      ) {
        return
      }

      tableShapedFeatureData.push({
        name: feature.title || '',
        core: false,
        holo: false,
        metal: true,
      })
    })
  }
  return !!tableShapedFeatureData ? tableShapedFeatureData : null
}
